import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useRef, useState, useCallback } from 'react';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenUserRanking from 'common/components/RewindModal/Konvas/ShareableScreenUserRanking';
import Slides from 'common/components/RewindModal/Slides';
import {
  SEIGNEUR_DES_NOTES,
  IMG_12_PERCENT
} from 'common/configuration/constants';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';

type ScreenUserRankingProps = {
  data: {
    nbr_rating: number;
    total_users: number;
    ranking_user: number;
  };
  onNext: () => void;
  isLastStep: boolean;
  step: number;
  steps: number;
  onStep: (step: number) => void;
  trackingScreenName?: string;
};

const ScreenUserRankingContent = ({
  onNext,
  isLastStep,
  isDownloadReady
}: ScreenUserRankingProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const title = `Le seigneur des notes`;
  const subtitle = `Comme Sauron, on vous appelle l'oeil qui voit tout.`;

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = 'mon-classement-allocine-2025.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Error handling for image generation
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  return (
    <>
      <Slides>
        <div
          className={classNames(styles.contentLayout, styles.centeredLayout)}
        >
          <div className={styles.userRankingImage}>
            <img
              src={SEIGNEUR_DES_NOTES}
              alt="seigneur des notes"
              className={styles.ratingLordImg}
            />
          </div>
          <div className={styles.titleCenterWrapper}>
            <div className={styles.slideTitle}>{title}</div>
            <div className={styles.slideText}>{subtitle}</div>
          </div>
          <div className={styles.userRankingImage}>
            <img
              src={IMG_12_PERCENT}
              alt="12%"
              className={styles.ratingPercentImg}
            />
          </div>
        </div>

        <BottomActions
          showShare={true}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          shareText={
            isDownloading ? 'Génération...' : 'Partager mon classement'
          }
          showNext={!isLastStep}
          onNextClick={onNext}
        />
      </Slides>

      <ShareableScreenUserRanking
        ref={stageRef}
        data={{
          title,
          subtitle
        }}
      />
    </>
  );
};

const ScreenUserRanking = (props: ScreenUserRankingProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenUserRankingContent {...props} isDownloadReady={isDownloadReady} />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenUserRanking;
